import type {
	API_MigrateScheduleMapping,
	Schedule,
} from "@pilatestime/shared-types";
import { Action } from "@pilatestime/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	AlertTriangle,
	ArrowRight,
	Building2,
	Calendar,
	ChevronDown,
	Clock,
	ExternalLink,
	Loader2,
	Search,
	Users,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnrollmentsBySchedule } from "@/api/enrollments";
import {
	fetchOrganizationSchedulesPaginatedQueryOptions,
	type ScheduleFilterParams,
	useMigrateSchedules,
} from "@/api/schedules";
import { fetchOrganizationStaffQueryOptions } from "@/api/staff";
import { DataTable } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Pagination, usePagination } from "@/components/ui/pagination";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/staff/$staffId/migrate-schedules",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, staffId } = params;
		return { userId, token, orgId, staffId };
	},
	component: MigrateSchedulesPage,
});

function ScheduleEnrollmentsPopover({
	scheduleId,
	token,
	organizationId,
}: {
	scheduleId: string;
	token: string;
	organizationId: string;
}) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const { data, isLoading } = useEnrollmentsBySchedule(
		token,
		organizationId,
		open ? scheduleId : "",
	);

	const enrollments = data?.enrollments ?? [];

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="btn btn-ghost btn-xs p-1"
					title={t("common.enrollments")}
					onClick={(e) => e.stopPropagation()}
				>
					<Users className="w-3.5 h-3.5" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="w-64 p-3"
				align="start"
				onClick={(e) => e.stopPropagation()}
			>
				<h4 className="text-sm font-medium mb-2">{t("common.enrollments")}</h4>
				{isLoading ? (
					<div className="flex items-center justify-center py-3">
						<Loader2 className="w-4 h-4 animate-spin text-base-content/50" />
					</div>
				) : enrollments.length === 0 ? (
					<p className="text-sm text-base-content/60">
						{t("common.noEnrollments")}
					</p>
				) : (
					<div className="space-y-1 max-h-48 overflow-y-auto">
						{enrollments.map((enrollment) => (
							<div
								key={enrollment.id}
								className="text-sm py-1 px-2 rounded bg-base-200/50"
							>
								<div className="font-medium">
									{enrollment.customer.firstName} {enrollment.customer.lastName}
								</div>
								{enrollment.customer.email && (
									<div className="text-xs text-base-content/60">
										{enrollment.customer.email}
									</div>
								)}
							</div>
						))}
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

type Assignment = {
	targetStaffMemberId: string;
	targetStaffName: string;
	scheduleName: string;
	scheduleType: string;
	scheduleTime: string;
};

function MigrateSchedulesPage() {
	const { t } = useTranslation();
	const { orgId, staffId } = Route.useParams();
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();
	const can = useCan();
	const canManageSchedules = can(Action.MANAGE_SCHEDULES);
	const migrateSchedulesMutation = useMigrateSchedules();

	// Pagination state
	const { page, pageSize, onPageChange, onPageSizeChange } = usePagination(
		1,
		20,
	);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	// Assignment state - persists across page changes
	const [assignments, setAssignments] = useState<Map<string, Assignment>>(
		new Map(),
	);

	// Selection state - current page only
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	// Target staff for bulk assign
	const [targetStaffId, setTargetStaffId] = useState("");

	// Expandable groups state for confirmation summary
	const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

	// Confirmation state
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [confirmText, setConfirmText] = useState("");

	// Search debounce
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchTerm(value);
			const timeout = setTimeout(() => {
				setDebouncedSearch(value);
				onPageChange(1);
			}, 300);
			return () => clearTimeout(timeout);
		},
		[onPageChange],
	);

	// Fetch paginated schedules filtered by staff member
	const filterParams: ScheduleFilterParams = useMemo(
		() => ({ staffMemberId: staffId }),
		[staffId],
	);

	const { data: schedulesResponse, isLoading } = useQuery(
		fetchOrganizationSchedulesPaginatedQueryOptions(
			userId,
			token,
			orgId,
			{ page, limit: pageSize },
			debouncedSearch || undefined,
			filterParams,
		),
	);

	const schedules = schedulesResponse?.data ?? [];
	const paginationMeta = schedulesResponse?.pagination ?? {
		page: 1,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	// Fetch staff members for target dropdown
	const { data: staffMembers } = useQuery(
		fetchOrganizationStaffQueryOptions(token, orgId),
	);

	const availableStaff = useMemo(
		() => staffMembers?.filter((member) => member.id !== staffId) ?? [],
		[staffMembers, staffId],
	);

	// Get target staff name
	const getStaffName = useCallback(
		(id: string) => {
			const member = staffMembers?.find((m) => m.id === id);
			if (!member) return id;
			return member.firstName && member.lastName
				? `${member.firstName} ${member.lastName}`
				: member.email;
		},
		[staffMembers],
	);

	// Selection handlers
	const handleToggleSelect = useCallback((scheduleId: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(scheduleId)) {
				next.delete(scheduleId);
			} else {
				next.add(scheduleId);
			}
			return next;
		});
	}, []);

	const handleToggleSelectAll = useCallback(() => {
		const pageIds = schedules.map((s) => s.id);
		const allSelected = pageIds.every((id) => selectedIds.has(id));
		if (allSelected) {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				for (const id of pageIds) {
					next.delete(id);
				}
				return next;
			});
		} else {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				for (const id of pageIds) {
					next.add(id);
				}
				return next;
			});
		}
	}, [schedules, selectedIds]);

	// Assign selected schedules to target
	const handleAssignSelected = useCallback(() => {
		if (!targetStaffId || selectedIds.size === 0) return;

		const targetName = getStaffName(targetStaffId);

		setAssignments((prev) => {
			const next = new Map(prev);
			for (const scheduleId of selectedIds) {
				const schedule = schedules.find((s) => s.id === scheduleId);
				next.set(scheduleId, {
					targetStaffMemberId: targetStaffId,
					targetStaffName: targetName,
					scheduleName: schedule?.name ?? scheduleId,
					scheduleType: schedule?.type ?? "",
					scheduleTime: schedule
						? `${schedule.startTime} - ${schedule.endTime}`
						: "",
				});
			}
			return next;
		});

		setSelectedIds(new Set());
	}, [targetStaffId, selectedIds, getStaffName, schedules]);

	// Remove assignment
	const handleRemoveAssignment = useCallback((scheduleId: string) => {
		setAssignments((prev) => {
			const next = new Map(prev);
			next.delete(scheduleId);
			return next;
		});
	}, []);

	// Page change handler - clear selections on page change
	const handlePageChange = useCallback(
		(newPage: number) => {
			setSelectedIds(new Set());
			onPageChange(newPage);
		},
		[onPageChange],
	);

	const handlePageSizeChange = useCallback(
		(newSize: number) => {
			setSelectedIds(new Set());
			onPageSizeChange(newSize);
		},
		[onPageSizeChange],
	);

	// Confirm migration
	const handleConfirmMigration = useCallback(async () => {
		const migrationAssignments: API_MigrateScheduleMapping[] = [];
		for (const [scheduleId, assignment] of assignments) {
			migrationAssignments.push({
				scheduleId,
				targetStaffMemberId: assignment.targetStaffMemberId,
			});
		}

		await migrateSchedulesMutation.mutateAsync({
			token,
			organizationId: orgId,
			sourceStaffId: staffId,
			data: { assignments: migrationAssignments },
		});

		navigate({
			to: "/app/org/$orgId/staff/$staffId",
			params: { orgId, staffId },
		});
	}, [assignments, migrateSchedulesMutation, token, orgId, staffId, navigate]);

	// Check if all page items are selected
	const pageIds = schedules.map((s) => s.id);
	const allPageSelected =
		pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
	const somePageSelected = pageIds.some((id) => selectedIds.has(id));

	// Table columns
	const columns: ColumnDef<Schedule>[] = useMemo(
		() => [
			{
				id: "select",
				header: () => (
					<Checkbox
						checked={allPageSelected}
						ref={(el) => {
							if (el) {
								(el as HTMLButtonElement).dataset.indeterminate = String(
									somePageSelected && !allPageSelected,
								);
							}
						}}
						onCheckedChange={handleToggleSelectAll}
						aria-label={t("common.selectAll")}
					/>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<Checkbox
							checked={selectedIds.has(schedule.id)}
							onCheckedChange={() => handleToggleSelect(schedule.id)}
							aria-label={t("common.select")}
						/>
					);
				},
				enableSorting: false,
			},
			{
				accessorKey: "name",
				header: () => (
					<div className="flex items-center gap-2">
						<Calendar className="w-4 h-4" />
						{t("common.name")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="flex items-center gap-2">
							<div className="flex-1 min-w-0">
								<div className="font-medium">{schedule.name}</div>
								{schedule.description && (
									<div className="text-sm text-base-content/70 truncate">
										{schedule.description}
									</div>
								)}
							</div>
							<div className="flex items-center gap-1 shrink-0">
								<ScheduleEnrollmentsPopover
									scheduleId={schedule.id}
									token={token}
									organizationId={orgId}
								/>
								<a
									href={`/app/org/${orgId}/schedules/${schedule.id}`}
									target="_blank"
									rel="noopener noreferrer"
									className="btn btn-ghost btn-xs p-1"
									title={t("common.details")}
									onClick={(e) => e.stopPropagation()}
								>
									<ExternalLink className="w-3.5 h-3.5" />
								</a>
							</div>
						</div>
					);
				},
			},
			{
				accessorKey: "type",
				header: t("common.type"),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<span className="badge badge-outline">
								{t(`common.${schedule.type.toLowerCase()}`)}
							</span>
						</div>
					);
				},
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "time",
				header: () => (
					<div className="flex items-center gap-2">
						<Clock className="w-4 h-4" />
						{t("common.time")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							<div className="font-medium">
								{schedule.startTime} - {schedule.endTime}
							</div>
							<div className="text-sm text-base-content/70">
								{schedule.daysOfWeek
									.map((day) => t(`common.${day.toLowerCase()}`))
									.join(", ")}
							</div>
						</div>
					);
				},
				meta: { hiddenOnMobile: true },
			},
			{
				accessorKey: "establishment",
				header: () => (
					<div className="flex items-center gap-2">
						<Building2 className="w-4 h-4" />
						{t("common.establishment")}
					</div>
				),
				cell: ({ row }) => {
					const schedule = row.original;
					return (
						<div className="text-center">
							{schedule.establishment?.name || "-"}
						</div>
					);
				},
				meta: { hiddenOnMobile: true },
			},
			{
				id: "status",
				header: t("common.status"),
				cell: ({ row }) => {
					const schedule = row.original;
					const assignment = assignments.get(schedule.id);
					if (assignment) {
						return (
							<div className="flex items-center gap-1 text-sm">
								<ArrowRight className="w-3 h-3 text-primary" />
								<span className="text-primary font-medium">
									{assignment.targetStaffName}
								</span>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										handleRemoveAssignment(schedule.id);
									}}
									className="ml-1 text-base-content/40 hover:text-error text-xs"
									title={t("common.remove")}
								>
									&times;
								</button>
							</div>
						);
					}
					return <span className="text-base-content/40">--</span>;
				},
			},
		],
		[
			t,
			token,
			orgId,
			selectedIds,
			assignments,
			allPageSelected,
			somePageSelected,
			handleToggleSelectAll,
			handleToggleSelect,
			handleRemoveAssignment,
		],
	);

	if (!canManageSchedules) {
		return (
			<div className="h-full flex items-center justify-center">
				<p className="text-base-content/60">{t("common.unauthorized")}</p>
			</div>
		);
	}

	if (showConfirmation) {
		return (
			<div className="h-full flex flex-col space-y-6">
				<Card className="border-0 shadow-lg">
					<CardContent className="p-6 space-y-6">
						{/* Warning Banner */}
						<div className="rounded-lg bg-warning/5 border border-warning/20 p-4">
							<div className="flex items-start gap-3">
								<AlertTriangle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
								<div>
									<p className="text-sm font-medium text-base-content">
										{t("staff.migrateSchedules.confirmationWarning", {
											count: assignments.size,
										})}
									</p>
									<p className="text-sm text-base-content/70 mt-1">
										{t("staff.migrateSchedules.confirmationDescription")}
									</p>
								</div>
							</div>
						</div>

						{/* Summary of assignments */}
						<div className="space-y-2">
							<h4 className="text-sm font-medium">
								{t("staff.migrateSchedules.assignmentSummary")}
							</h4>
							<div className="space-y-1">
								{(() => {
									const grouped = new Map<
										string,
										{
											scheduleName: string;
											scheduleType: string;
											scheduleTime: string;
										}[]
									>();
									for (const [, assignment] of assignments) {
										const existing = grouped.get(assignment.targetStaffName);
										const detail = {
											scheduleName: assignment.scheduleName,
											scheduleType: assignment.scheduleType,
											scheduleTime: assignment.scheduleTime,
										};
										if (existing) {
											existing.push(detail);
										} else {
											grouped.set(assignment.targetStaffName, [detail]);
										}
									}
									return Array.from(grouped.entries()).map(
										([staffName, scheduleDetails]) => {
											const isOpen = expandedGroups.has(staffName);
											return (
												<div
													key={staffName}
													className="bg-base-200/50 rounded overflow-hidden"
												>
													<button
														type="button"
														className="flex items-center gap-2 text-sm p-2 w-full text-left"
														onClick={() =>
															setExpandedGroups((prev) => {
																const next = new Set(prev);
																if (next.has(staffName)) {
																	next.delete(staffName);
																} else {
																	next.add(staffName);
																}
																return next;
															})
														}
													>
														<ChevronDown
															className={`w-3.5 h-3.5 text-base-content/50 shrink-0 transition-transform duration-300 ${
																isOpen ? "rotate-180" : ""
															}`}
														/>
														<ArrowRight className="w-3 h-3 text-primary shrink-0" />
														<span className="font-medium text-primary">
															{staffName}
														</span>
														<span className="text-base-content/60">
															({scheduleDetails.length}{" "}
															{t("staff.migrateSchedules.schedulesCount", {
																count: scheduleDetails.length,
															})}
															)
														</span>
													</button>
													<div
														className="grid transition-all duration-300 ease-in-out"
														style={{
															gridTemplateRows: isOpen ? "1fr" : "0fr",
														}}
													>
														<div className="overflow-hidden">
															<div className="px-3 pb-2 space-y-1">
																{scheduleDetails.map((detail) => (
																	<div
																		key={detail.scheduleName}
																		className="flex items-center gap-2 text-sm py-1 px-2 bg-base-100/60 rounded"
																	>
																		<span className="font-medium text-base-content">
																			{detail.scheduleName}
																		</span>
																		{detail.scheduleType && (
																			<span className="badge badge-outline badge-xs">
																				{t(
																					`common.${detail.scheduleType.toLowerCase()}`,
																				)}
																			</span>
																		)}
																		{detail.scheduleTime && (
																			<span className="text-xs text-base-content/50">
																				{detail.scheduleTime}
																			</span>
																		)}
																	</div>
																))}
															</div>
														</div>
													</div>
												</div>
											);
										},
									);
								})()}
							</div>
						</div>

						{/* Confirmation Input */}
						<div className="space-y-2">
							<label
								htmlFor="migrate-confirm-input"
								className="text-sm font-medium text-base-content"
							>
								{t("staff.migrateSchedules.confirmLabel")}
							</label>
							<Input
								id="migrate-confirm-input"
								value={confirmText}
								onChange={(e) => setConfirmText(e.target.value)}
								placeholder={t("staff.migrateSchedules.confirmPlaceholder")}
								disabled={migrateSchedulesMutation.isPending}
							/>
						</div>

						{/* Action Buttons */}
						<div className="flex justify-end gap-3">
							<Button
								variant="outline"
								onClick={() => {
									setShowConfirmation(false);
									setConfirmText("");
								}}
								disabled={migrateSchedulesMutation.isPending}
							>
								{t("common.back")}
							</Button>
							<Button
								className="text-primary-content"
								onClick={handleConfirmMigration}
								disabled={
									confirmText !== "confirm" ||
									migrateSchedulesMutation.isPending
								}
							>
								{migrateSchedulesMutation.isPending
									? t("staff.migrateSchedules.toasts.migrating")
									: t("staff.migrateSchedules.confirmButton")}
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="h-full flex flex-col space-y-4">
			{/* Search & Bulk Assign Controls */}
			<Card className="border-0 shadow-lg shrink-0">
				<CardContent className="p-4 space-y-4">
					{/* Search */}
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-base-content/50" />
						<Input
							className="pl-10"
							placeholder={t("staff.migrateSchedules.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => handleSearchChange(e.target.value)}
						/>
					</div>

					{/* Bulk Assign Row */}
					<div className="flex items-center gap-3 flex-wrap">
						<span className="text-sm font-medium text-base-content/70 shrink-0">
							{t("staff.migrateSchedules.assignSelectedTo")}
						</span>
						<Select value={targetStaffId} onValueChange={setTargetStaffId}>
							<SelectTrigger className="w-60">
								<SelectValue
									placeholder={t(
										"staff.migrateSchedules.selectTargetPlaceholder",
									)}
								/>
							</SelectTrigger>
							<SelectContent>
								{availableStaff.map((member) => (
									<SelectItem key={member.id} value={member.id}>
										{member.firstName && member.lastName
											? `${member.firstName} ${member.lastName}`
											: member.email}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Button
							size="sm"
							onClick={handleAssignSelected}
							disabled={selectedIds.size === 0 || !targetStaffId}
						>
							{t("staff.migrateSchedules.assignButton")} ({selectedIds.size})
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Table */}
			<div className="flex-1 min-h-0">
				<DataTable
					data={schedules}
					isLoading={isLoading}
					error={null}
					columns={columns}
					testId="migrate-schedules-table"
					canManage={false}
					emptyState={{
						title: t("staff.schedules.noSchedules"),
						description: t("staff.schedules.noSchedulesDescription"),
					}}
				/>
			</div>

			{/* Pagination */}
			{paginationMeta.total > 0 && (
				<div className="shrink-0">
					<Pagination
						pagination={paginationMeta}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						pageSizeOptions={[10, 20, 50]}
					/>
				</div>
			)}

			{/* Footer: assignment count + continue */}
			<div className="shrink-0 border-t border-base-200 pt-4 flex items-center justify-between">
				<span className="text-sm text-base-content/70">
					{assignments.size > 0
						? t("staff.migrateSchedules.assignedCount", {
								count: assignments.size,
							})
						: t("staff.migrateSchedules.noAssignments")}
				</span>
				<Button
					onClick={() => setShowConfirmation(true)}
					disabled={assignments.size === 0}
				>
					{t("staff.migrateSchedules.continueButton")}
					<ArrowRight className="ml-2 h-4 w-4" />
				</Button>
			</div>
		</div>
	);
}
